import React, { useState, useMemo } from 'react';
import RequisitionForm from '../../components/Form/RequisitionForm';
import RequisitionItemsTable from './RequisitionItemsTable';
import RequisitionItemForm from './RequisitionItemForm';
import ApproveItemDialog from './ApproveItemDialog';
import { PageHeader, StatsCards, ErrorState } from '../../components/Common';
import { Requisition, RequisitionItem } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useEmployees } from '../../hooks/useEmployees';
import { useRequisitions } from '../../hooks/useRequisitions';
import { useRequisitionWorkflow } from './useRequisitionWorkflow';
import { ItemRow, statusConfig, EMPTY_ITEM, rowKey } from './requisitionWorkflowHelpers';

const RequisitionsNew: React.FC = () => {
  const [vesselFilter, setVesselFilter] = useState('all');
  const [requisitionFilter, setRequisitionFilter] = useState('all');

  const {
    requisitions, rows, isLoading, error, refetch, providers,
    updateItemStatus, createDeliveryNoteForRows, createPaymentOrderForRows, saveItem, deleteItem,
    createRequisition, updateRequisition, deleteRequisition,
  } = useRequisitionWorkflow({ vessel: vesselFilter, requisitionId: requisitionFilter });

  const supplierOptions = useMemo(() =>
    (providers as any[])
      .filter((p: any) => p.name)
      .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)))
      .map((p: any) => ({ value: p.name, label: p.name })),
    [providers]);

  // Unfiltered list so the filter dropdowns always show every option
  const { data: allRequisitions = [], refetch: refetchAll } = useRequisitions();
  const vesselFilterOptions = useMemo(() => {
    const names = Array.from(new Set((allRequisitions as any[]).map((r: any) => r.vessel).filter(Boolean)));
    return names.sort((a, b) => String(a).localeCompare(String(b)));
  }, [allRequisitions]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [showReqForm, setShowReqForm] = useState(false);
  const [editingReq, setEditingReq] = useState<Requisition | null>(null);
  const [editingRow, setEditingRow] = useState<ItemRow | null>(null);
  const [newItem, setNewItem] = useState({ ...EMPTY_ITEM });
  const [targetReqId, setTargetReqId] = useState('');
  const [approvingRow, setApprovingRow] = useState<ItemRow | null>(null);

  const { data: employees = [] } = useEmployees();
  const employeeOptions = useMemo(() =>
    (employees as any[])
      .filter((e: any) => e.name)
      .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)))
      .map((e: any) => ({ value: e.name, label: e.name })),
    [employees]);

  const itemRows = useMemo(() => rows.filter(r => !r.placeholder), [rows]);
  const selectedRows = useMemo(() => itemRows.filter(r => selectedIds.has(rowKey(r))), [itemRows, selectedIds]);

  const toggleSelect = (row: ItemRow) => {
    const key = rowKey(row);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(prev =>
      itemRows.every(r => prev.has(rowKey(r))) ? new Set() : new Set(itemRows.map(rowKey)));
  };

  const resetForm = () => { setShowAddForm(false); setEditingRow(null); setNewItem({ ...EMPTY_ITEM }); setTargetReqId(''); };

  const handleEditItem = (row: ItemRow) => {
    setEditingRow(row);
    const { item } = row;
    setNewItem({ item: item.item, qty: item.qty, rob: item.rob || '', appQty: item.appQty || 0, unitPrice: item.unitPrice || 0, supplier: item.supplier || '', remarks: item.remarks || '', preparedBy: item.preparedBy || '' });
    setShowAddForm(true);
  };

  // Approving requires picking the employee who will handle the payment order
  const handleStatusChange = (row: ItemRow, status: RequisitionItem['status']) => {
    if (status === 'approved') setApprovingRow(row);
    else updateItemStatus(row.requisition, row.item, status);
  };

  const handleApproveConfirm = async (employeeName: string) => {
    if (!approvingRow) return;
    await updateItemStatus(approvingRow.requisition, approvingRow.item, 'approved', employeeName);
    setApprovingRow(null);
  };

  const handleItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingRow) {
      const ok = await saveItem(editingRow.requisition, newItem, editingRow.item.id);
      if (ok) resetForm();
      return;
    }
    const req = requisitions.find(r => (r.documentId || String(r.id)) === targetReqId);
    if (!req) return;
    const ok = await saveItem(req, newItem);
    if (ok) resetForm();
  };

  const handleDeleteItem = (row: ItemRow) => {
    if (window.confirm(`Delete item "${row.item.item}"?`)) deleteItem(row.requisition, row.item.id);
  };

  const handleDeleteRequisition = async (row: ItemRow) => {
    const req = row.requisition;
    if (window.confirm(`Delete requisition ${req.formNumber} and all its items?`)) {
      await deleteRequisition(req);
      refetchAll();
    }
  };

  // Warn before acting on a selection that spans several requisitions —
  // one document is created per requisition, never one mixing them
  const confirmMixedSelection = (docLabel: string): boolean => {
    const reqNumbers = Array.from(new Set(selectedRows.map(r => r.requisition.formNumber)));
    if (reqNumbers.length <= 1) return true;
    return window.confirm(
      `Warning: the selected items belong to ${reqNumbers.length} different requisitions (${reqNumbers.join(', ')}).\n\n` +
      `A separate ${docLabel} will be created for EACH requisition. Continue?`);
  };

  const handleCreateDeliveryNote = async () => {
    if (selectedRows.length === 0 || !confirmMixedSelection('delivery note')) return;
    await createDeliveryNoteForRows(selectedRows);
    setSelectedIds(new Set());
  };

  const handleCreatePaymentOrder = async () => {
    if (selectedRows.length === 0 || !confirmMixedSelection('payment order')) return;
    await createPaymentOrderForRows(selectedRows);
    setSelectedIds(new Set());
  };

  // "PO Req" button: one payment order covering every item of that requisition
  const handleCreatePOForReq = async (row: ItemRow) => {
    const reqId = row.requisition.documentId || String(row.requisition.id);
    const rowsOfReq = itemRows.filter(r => (r.requisition.documentId || String(r.requisition.id)) === reqId);
    if (rowsOfReq.length === 0) return;
    if (!window.confirm(`Create one payment order for requisition ${row.requisition.formNumber} with all ${rowsOfReq.length} item(s)?`)) return;
    await createPaymentOrderForRows(rowsOfReq);
  };

  const handleReqFormSubmit = async (data: Partial<Requisition>) => {
    const ok = editingReq
      ? await updateRequisition(editingReq, data)
      : await createRequisition(data);
    if (ok) { setShowReqForm(false); setEditingReq(null); refetchAll(); }
  };

  const handleAddItemToReq = (row: ItemRow) => {
    setEditingRow(null);
    setNewItem({ ...EMPTY_ITEM });
    setTargetReqId(row.requisition.documentId || String(row.requisition.id));
    setShowAddForm(true);
  };

  const buildExportData = () => itemRows.map(({ item, requisition }) => ({
    'Item #': item.itemNumber, 'Item': item.item, 'Requisition': requisition.formNumber,
    'Vessel': requisition.vessel, 'Quantity': item.qty, 'ROB': item.rob,
    'Approved Qty': item.appQty, 'Unit Price': item.unitPrice, 'Supplier': item.supplier,
    'Status': item.status, 'Remarks': item.remarks || '',
  }));

  const stats = Object.entries(statusConfig).map(([key, cfg]) => ({
    label: cfg.label, value: itemRows.filter(r => r.item.status === key).length,
    color: (key === 'pending' ? 'yellow' : key === 'rejected' ? 'red' : key === 'approved' ? 'green' : key === 'delivery-noted' ? 'purple' : 'blue') as any,
  }));

  if (showReqForm || editingReq) return (
    <div className="space-y-6">
      <RequisitionForm requisition={editingReq || undefined} onSubmit={handleReqFormSubmit}
        onCancel={() => { setShowReqForm(false); setEditingReq(null); }} userRole="office" />
    </div>
  );
  if (isLoading) return (
    <div className="flex items-center justify-center h-64"><div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600 mx-auto mb-4"></div><p className="text-gray-600">Loading requisitions...</p></div></div>
  );
  if (error) return <ErrorState message="Error loading requisitions" onRetry={refetch} entityName="requisitions" />;

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Requisition Management" description="Manage requisition items and their status workflow" />

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Vessel / HQ</label>
            <select value={vesselFilter} onChange={(e) => setVesselFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500">
              <option value="all">All Vessels / HQs</option>
              {vesselFilterOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="min-w-[240px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Requisition</label>
            <select value={requisitionFilter} onChange={(e) => setRequisitionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500">
              <option value="all">All Requisitions</option>
              {(allRequisitions as any[])
                .filter((r: any) => vesselFilter === 'all' || r.vessel === vesselFilter)
                .map((r: any) => (
                  <option key={r.documentId || r.id} value={r.documentId || String(r.id)}>
                    {r.formNumber} — {r.vessel}
                  </option>
                ))}
            </select>
          </div>
          {(vesselFilter !== 'all' || requisitionFilter !== 'all') && (
            <button onClick={() => { setVesselFilter('all'); setRequisitionFilter('all'); }}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Clear Filters
            </button>
          )}
        </div>
      </div>

      <StatsCards stats={stats} columns={3} />

      <div className="card p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <span className="text-sm text-gray-600">{selectedRows.length} item(s) selected</span>
          <div className="flex flex-wrap gap-2">
            <button onClick={handleCreatePaymentOrder} disabled={selectedRows.length === 0} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium">
              Create Payment Order ({selectedRows.length})
            </button>
            <button onClick={handleCreateDeliveryNote} disabled={selectedRows.length === 0} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium">
              Create Delivery Note ({selectedRows.length})
            </button>
            <button onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }} className="px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 font-medium">
              {showAddForm ? 'Cancel' : '+ Add New Item'}
            </button>
            <button onClick={() => setShowReqForm(true)} className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium">
              + New Requisition
            </button>
            <button onClick={() => exportToExcel(buildExportData(), `requisition_items_${new Date().toISOString().split('T')[0]}`)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">Export</button>
            <button onClick={() => printAsExcel(buildExportData(), 'Requisition Items')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Print</button>
          </div>
        </div>
      </div>

      {showAddForm && (
        <RequisitionItemForm
          requisitions={requisitions}
          editing={!!editingRow}
          values={newItem}
          onChange={setNewItem}
          requisitionId={targetReqId}
          onRequisitionChange={setTargetReqId}
          onSubmit={handleItemSubmit}
          onCancel={resetForm}
          employeeOptions={employeeOptions}
          supplierOptions={supplierOptions}
        />
      )}

      <RequisitionItemsTable
        rows={rows}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleAll={toggleAll}
        onStatusChange={handleStatusChange}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        onAddItem={handleAddItemToReq}
        onEditRequisition={(row) => setEditingReq(row.requisition)}
        onDeleteRequisition={handleDeleteRequisition}
        onCreatePaymentOrder={handleCreatePOForReq}
      />

      {approvingRow && (
        <ApproveItemDialog
          itemName={approvingRow.item.item}
          employeeOptions={employeeOptions}
          defaultEmployee={approvingRow.item.preparedBy || ''}
          onConfirm={handleApproveConfirm}
          onCancel={() => setApprovingRow(null)}
        />
      )}
    </div>
  );
};

export default RequisitionsNew;
